import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Plus, X, Edit2, Check, User, Target, Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const ROLES = ['admin', 'tl', 'consultant', 'legal', 'finance', 'hr'];
const ROLE_LABELS = { admin: '👑 Admin', tl: '👨‍💼 Team Lead', consultant: '🧑‍💻 Consultant', legal: '⚖️ Legal', finance: '💰 Finance', hr: '🧑‍🤝‍🧑 HR' };

// Add/Edit User Modal
function UserModal({ user, onClose, onSave, allUsers }) {
  const isEdit = !!user;
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    password: '',
    role: user?.role || 'consultant',
    phone: user?.phone || '',
    monthlyTarget: user?.monthlyTarget || 50000,
    tlId: user?.tlId || '',
    doj: user?.doj || new Date().toISOString().split('T')[0],
    smartflo_did: user?.smartflo_did || '',
    smartflo_api_key: user?.smartflo_api_key || '',
    active: user?.active !== false,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const tls = allUsers.filter(u => u.role === 'tl');

  const save = async () => {
    if (!form.name || !form.email) return toast.error('Name aur email required');
    if (!isEdit && !form.password) return toast.error('Password required');
    setSaving(true);
    try {
      if (isEdit) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.patch(`/users/${user.id}`, payload);
        toast.success('User updated!');
      } else {
        await api.post('/users', form);
        toast.success('User created!');
      }
      onSave();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <div className="modal-title">{isEdit ? 'Edit User' : 'Add New User'}</div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="grid grid-2">
            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <input className="form-control" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Rahul Sharma" />
            </div>
            <div className="form-group">
              <label className="form-label">Email *</label>
              <input className="form-control" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="rahul@easysettlement.in" />
            </div>
            <div className="form-group">
              <label className="form-label">{isEdit ? 'New Password (leave blank to keep)' : 'Password *'}</label>
              <input className="form-control" type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="••••••••" />
            </div>
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input className="form-control" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="9XXXXXXXXX" />
            </div>
            <div className="form-group">
              <label className="form-label">Role *</label>
              <select className="form-control" value={form.role} onChange={e => set('role', e.target.value)}>
                {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select>
            </div>
            {form.role === 'consultant' && (
              <div className="form-group">
                <label className="form-label">Team Lead</label>
                <select className="form-control" value={form.tlId} onChange={e => set('tlId', e.target.value)}>
                  <option value="">No TL assigned</option>
                  {tls.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            )}
            {['consultant', 'tl'].includes(form.role) && (
              <div className="form-group">
                <label className="form-label">Monthly Target (₹)</label>
                <input className="form-control" type="number" value={form.monthlyTarget} onChange={e => set('monthlyTarget', Number(e.target.value))} />
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Date of Joining</label>
              <input className="form-control" type="date" value={form.doj} onChange={e => set('doj', e.target.value)} />
            </div>

            {/* Smartflo / Tata Tele */}
            <div style={{ padding: '12px 0 4px', borderTop: '1px solid var(--border)', marginTop: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>📞 Tata Tele / Smartflo</div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Smartflo DID Number</label>
                  <input className="form-control" value={form.smartflo_did} onChange={e => set('smartflo_did', e.target.value)} placeholder="918065XXXXXX" />
                </div>
                <div className="form-group">
                  <label className="form-label">Smartflo API Key</label>
                  <input className="form-control" value={form.smartflo_api_key} onChange={e => set('smartflo_api_key', e.target.value)} placeholder="eyJ0eXAi..." type="password" />
                </div>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: -8 }}>
                Phone number (above) = Call Forward Number in Smartflo
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-control" value={form.active} onChange={e => set('active', e.target.value === 'true')}>
                <option value="true">✅ Active</option>
                <option value="false">❌ Inactive</option>
              </select>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? <span className="spinner" /> : isEdit ? 'Update User' : 'Create User'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tata Tele Settings Component ──────────────────────────────────────────
function TataTeleSettings() {
  const [config, setConfig] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/calls/config').then(r => setConfig(r.data)).catch(() => {}),
      api.get('/users').then(r => setUsers(r.data.filter(u => !u.archived && ['admin','consultant','tl','legal'].includes(u.role))))
    ]).finally(() => setLoading(false));
  }, []);

  const updatePhone = async (userId, phone) => {
    await api.patch(`/users/${userId}`, { phone });
    toast.success('Phone number updated!');
    api.get('/users').then(r => setUsers(r.data.filter(u => !u.archived && ['consultant','tl'].includes(u.role))));
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;

  return (
    <div>
      {/* Config status */}
      <div className="card mb-16">
        <h3 style={{ fontSize: 15, marginBottom: 16 }}>📞 Tata Tele Click-to-Call</h3>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px 16px', background: config?.configured ? '#f0fdf4' : '#fef2f2', borderRadius: 8, marginBottom: 16 }}>
          <div style={{ fontSize: 24 }}>{config?.configured ? '✅' : '❌'}</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: config?.configured ? 'var(--success)' : 'var(--danger)' }}>
              {config?.configured ? 'Tata Tele Configured' : 'Not Configured'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              {config?.configured
                ? `DID Number: ${config.did} · API Key: Set in Railway`
                : 'Railway pe TATA_TELE_API_KEY aur TATA_TELE_DID set karo'
              }
            </div>
          </div>
        </div>

        <div style={{ padding: '12px 16px', background: 'var(--surface)', borderRadius: 8, fontSize: 13 }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Railway Environment Variables:</div>
          <div style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)', lineHeight: 2 }}>
            <div>TATA_TELE_API_KEY = <span style={{ color: 'var(--success)' }}>your_api_key</span></div>
            <div>TATA_TELE_DID = <span style={{ color: 'var(--success)' }}>your_did_number</span></div>
          </div>
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
            API key change karna ho → Railway dashboard → Service → Variables → Update karo → Auto-redeploy
          </div>
        </div>
      </div>

      {/* Agent phone numbers */}
      <div className="card">
        <h3 style={{ fontSize: 15, marginBottom: 4 }}>📱 Agent Phone Numbers</h3>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            Update when an executive is replaced. This number is used as agent number in Click-to-Call.
          </div>

        {users.map(u => (
          <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gold-light)', fontWeight: 700, flexShrink: 0 }}>
              {u.name?.[0]}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{u.name}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'capitalize' }}>{u.role}</div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                defaultValue={u.phone || ''}
                placeholder="9XXXXXXXXX"
                style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, width: 140 }}
                onBlur={e => {
                  const val = e.target.value.trim();
                  if (val !== (u.phone || '') && val) updatePhone(u.id, val);
                }}
              />
              {u.phone
                ? <span className="badge badge-green" style={{ fontSize: 10 }}>✓ Set</span>
                : <span className="badge badge-red" style={{ fontSize: 10 }}>⚠ Missing</span>
              }
            </div>
          </div>
        ))}

        {users.length === 0 && (
          <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)', fontSize: 13 }}>
            No consultants or TLs found
          </div>
        )}
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [tab, setTab] = useState('users');
  const { user: currentUser } = useAuth();

  const load = () => {
    setLoading(true);
    api.get('/users').then(r => setUsers(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const resetPassword = async (u) => {
    const choice = window.confirm(`Reset password for ${u.name}?\n\nClick OK to set a new password manually.\nClick Cancel to generate a temporary password.`);
    if (choice) {
      const newPassword = window.prompt(`Enter new password for ${u.name} (min 6 characters):`);
      if (!newPassword || newPassword.length < 6) return toast.error('Password must be at least 6 characters');
      try {
        await api.post(`/users/${u.id}/reset-password`, { newPassword });
        toast.success(`Password reset successfully for ${u.name}`);
      } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
    } else {
      try {
        const r = await api.post(`/users/${u.id}/generate-temp-password`);
        toast.success(`Temporary password: ${r.data.tempPassword}`, { duration: 10000 });
        alert(`Temporary password for ${u.name}:\n\n${r.data.tempPassword}\n\nShare this with the user. They should change it after login.`);
      } catch (e) { toast.error('Failed'); }
    }
  };

  const deactivate = async (u) => {
    if (u.id === currentUser.id) return toast.error('Apna account deactivate nahi kar sakte');
    await api.patch(`/users/${u.id}`, { active: false, archived: true });
    toast.success('User deactivated');
    load();
  };

  const consultants = users.filter(u => u.role === 'consultant');
  const tls = users.filter(u => u.role === 'tl');
  const admins = users.filter(u => u.role === 'admin');
  const legals = users.filter(u => u.role === 'legal');
  const hrs = users.filter(u => u.role === 'hr');
  const finances = users.filter(u => u.role === 'finance');

  const userGroups = [
    { label: '👑 Administrators', users: admins },
    { label: '👨‍💼 Team Leaders', users: tls },
    { label: '🧑‍💻 Consultants / BDE', users: consultants },
    { label: '⚖️ Legal Team', users: legals },
    { label: '🧑‍🤝‍🧑 HR', users: hrs },
    { label: '💰 Finance / Accounts', users: finances },
  ].filter(g => g.users.length > 0);

  return (
    <div className="page">
      <div className="flex-between mb-24">
        <h2 style={{ fontSize: 18 }}>Settings</h2>
        <button className="btn btn-primary" onClick={() => { setEditUser(null); setShowModal(true); }}>
          <Plus size={16} /> Add User
        </button>
      </div>

      <div className="tabs">
        {[
          { key: 'users', label: '👥 User Management' },
          { key: 'targets', label: '🎯 Monthly Targets' },
          { key: 'tatatele', label: '📞 Tata Tele' },
          { key: 'info', label: 'ℹ️ Company Info' },
        ].map(t => <button key={t.key} className={`tab-btn ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>{t.label}</button>)}
      </div>

      {/* User Management Tab */}
      {tab === 'users' && (
        <div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : (
            <>
              {userGroups.map(group => (
                <div key={group.label} style={{ marginBottom: 28 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {group.label} ({group.users.length})
                  </div>
                  {group.users.map(u => (
                    <div key={u.id} className="card mb-8" style={{ padding: '12px 16px', opacity: u.active === false ? 0.6 : 1 }}>
                      <div className="flex-between">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gold-light)', fontWeight: 700 }}>
                            {u.name?.[0]}
                          </div>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontWeight: 600, fontSize: 14 }}>{u.name}</span>
                              <span className="badge badge-navy" style={{ fontSize: 10 }}>{ROLE_LABELS[u.role] || u.role}</span>
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                              {u.email}
                              {u.phone && ` · ${u.phone}`}
                              {u.monthlyTarget && ` · Target: ₹${u.monthlyTarget.toLocaleString('en-IN')}`}
                              {u.tlId && ` · TL: ${tls.find(t => t.id === u.tlId)?.name || '—'}`}
                              {u.role === 'tl' && (() => {
                                const team = consultants.filter(c => c.tlId === u.id).map(c => c.name).join(', ');
                                return team ? ` · Team: ${team}` : '';
                              })()}
                              {u.doj && ` · DOJ: ${new Date(u.doj).toLocaleDateString('en-IN')}`}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <span className={`badge ${u.active !== false ? 'badge-green' : 'badge-red'}`}>
                            {u.active !== false ? 'Active' : 'Inactive'}
                          </span>
                          <button className="btn btn-ghost btn-sm" onClick={() => { setEditUser(u); setShowModal(true); }}>
                            <Edit2 size={13} /> Edit
                          </button>
                          <button className="btn btn-ghost btn-sm" onClick={() => resetPassword(u)} title="Reset Password">
                            🔑 Reset
                          </button>
                          {u.id !== currentUser.id && u.active !== false && (
                            <button className="btn btn-danger btn-sm" onClick={() => deactivate(u)}>Deactivate</button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* Monthly Targets Tab */}
      {tab === 'targets' && (
        <div className="card">
          <div className="flex-between mb-20">
            <h3 style={{ fontSize: 15 }}>Monthly Targets</h3>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Auto-synced from user profiles. Click outside field to save.</div>
          </div>
          {users.filter(u => ['consultant', 'tl'].includes(u.role) && u.active !== false).map(u => (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gold-light)', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                {u.name?.[0]}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{u.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'capitalize' }}>{u.role}</div>
              </div>
              <input
                type="number"
                className="form-control"
                style={{ width: 140, textAlign: 'right' }}
                defaultValue={u.monthlyTarget || 50000}
                onBlur={async (e) => {
                  await api.patch(`/users/${u.id}`, { monthlyTarget: Number(e.target.value) });
                  toast.success(`Target updated for ${u.name}`);
                  load();
                }}
              />
            </div>
          ))}
          {users.filter(u => ['consultant','tl'].includes(u.role) && u.active !== false).length === 0 && (
            <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)', fontSize: 13 }}>
              No consultants or TLs found. Add users first.
            </div>
          )}
        </div>
      )}

      {/* Company Info Tab */}
      {tab === 'info' && (
        <div className="card">
          <h3 style={{ fontSize: 15, marginBottom: 20 }}>Company Information</h3>
          {[
            { label: 'Company Name', value: 'Easy Settlement / Lawlance Legal Services Pvt. Ltd.' },
            { label: 'Legal Entity', value: 'Demand Defence LLP' },
            { label: 'Address', value: 'B-41, Sector 2, Noida, UP — 201301' },
            { label: 'Phone', value: '0120-4894731' },
            { label: 'Legal Email', value: 'legal@demanddefence.com' },
            { label: 'Website', value: 'easysettlement.in' },
          ].map(item => (
            <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
              <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{item.label}</span>
              <span>{item.value}</span>
            </div>
          ))}
          <div style={{ marginTop: 16, padding: '10px 12px', background: 'var(--surface)', borderRadius: 8, fontSize: 12, color: 'var(--text-muted)' }}>
            Company details edit karne ke liye backend ke <code>seed.js</code> mein update karo aur reseed karo.
          </div>
        </div>
      )}

      {/* Tata Tele Tab */}
      {tab === 'tatatele' && <TataTeleSettings />}

      {showModal && (
        <UserModal
          user={editUser}
          allUsers={users}
          onClose={() => { setShowModal(false); setEditUser(null); }}
          onSave={() => { setShowModal(false); setEditUser(null); load(); }}
        />
      )}
    </div>
  );
}
